import { useEffect, useMemo, useRef, useState } from "react";
import { useApiFetch } from "../../api/client";
import { Btn } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import { errorMessage } from "../cockpit/format";
import { createGroup, listGroups, normalizeGroupName, sortGroups } from "./groupsApi";
import { LocationCheckPanel } from "./LocationCheckPanel";
import { PlantNameField } from "./PlantNameField";
import type { FacilityDetail, PlantNameChange, PlantNameSource, PolymerGroupView } from "./types";

interface Props {
  facilityName: string;
  originalName?: string | null;
  confirmLabel: string;
  confirmIcon?: string;
  initialIds: readonly number[];
  canAdd: boolean;
  locationRegistryId?: string;
  canCheckLocation?: boolean;
  onLocationApplied?: (detail: FacilityDetail) => void;
  onConfirm: (groupIds: number[], rename?: PlantNameChange) => void;
  onClose: () => void;
}

export function GroupPickerDialog({
  facilityName,
  originalName = null,
  confirmLabel,
  confirmIcon = "verified",
  initialIds,
  canAdd,
  locationRegistryId,
  canCheckLocation = false,
  onLocationApplied,
  onConfirm,
  onClose,
}: Props) {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const [groups, setGroups] = useState<PolymerGroupView[] | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set(initialIds));
  const [filter, setFilter] = useState("");
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [plantName, setPlantName] = useState(facilityName);
  const [nameSource, setNameSource] = useState<PlantNameSource>("Manual");
  const canRename = Boolean(locationRegistryId) && canCheckLocation;
  const renamed = canRename && plantName.trim() !== "" && plantName.trim() !== facilityName;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const loaded = await listGroups(apiFetchRef.current);
        if (!cancelled) setGroups(loaded);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const shown = useMemo(() => {
    const wanted = normalizeGroupName(filter);
    return (groups ?? []).filter((g) => !wanted || normalizeGroupName(g.name).includes(wanted));
  }, [groups, filter]);

  const toggle = (id: number) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const select = (id: number) => setSelected((current) => new Set(current).add(id));

  const add = async () => {
    const name = newName.trim();
    if (!name || !groups) return;
    setError(null);
    setNotice(null);
    const existing = groups.find((g) => normalizeGroupName(g.name) === normalizeGroupName(name));
    if (existing) {
      select(existing.polymerGroupId);
      setNewName("");
      setFilter("");
      setNotice(`${existing.name} is already in the list, so it was selected.`);
      return;
    }
    setAdding(true);
    try {
      const made = await createGroup(apiFetchRef.current, name);
      setGroups((current) => sortGroups([...(current ?? []), made]));
      select(made.polymerGroupId);
      setNewName("");
      setFilter("");
      setNotice(`${made.name} was added and selected.`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setAdding(false);
    }
  };

  const chosen = (groups ?? []).filter((g) => selected.has(g.polymerGroupId)).map((g) => g.polymerGroupId);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-[rgba(12,14,10,0.65)] z-[1100] p-6"
      role="presentation"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <Card
        className="w-full max-w-xl max-h-full overflow-auto"
        role="dialog"
        aria-modal="true"
        aria-label="Polymer groups"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="m-0 mb-1 text-lg">Polymer Groups</h3>
        <p className="mt-0 mb-4 text-[13px] text-rs-light-gray">
          Choose every group {facilityName} belongs to. They fill the Groups column of the export.
        </p>

        {locationRegistryId && (
          <div className="mb-4 rounded-md border border-rs-jet-light p-3">
            <LocationCheckPanel
              registryId={locationRegistryId}
              canCheck={canCheckLocation}
              autoRun
              onApplied={onLocationApplied}
              onUseName={
                canRename
                  ? (name) => {
                      setPlantName(name);
                      setNameSource("Map");
                    }
                  : undefined
              }
            />
          </div>
        )}

        {canRename && (
          <div className="mb-4">
            <PlantNameField
              value={plantName}
              currentName={facilityName}
              originalName={originalName}
              source={nameSource}
              onChange={(name, source) => {
                setPlantName(name);
                setNameSource(source);
              }}
            />
          </div>
        )}

        {error && <p className="text-rs-red text-[13px] mt-0 mb-3">{error}</p>}
        {notice && (
          <p className="text-rs-green text-[13px] mt-0 mb-3" role="status">
            {notice}
          </p>
        )}

        <Input
          className="mb-3"
          value={filter}
          placeholder="Filter groups"
          aria-label="Filter groups"
          onChange={(e) => setFilter(e.target.value)}
        />

        <div className="card p-3 overflow-auto max-h-[300px] mb-3">
          {!groups && !error && <p className="m-0 text-[13px] text-rs-light-gray">Loading…</p>}
          {groups && shown.length === 0 && (
            <p className="m-0 text-[13px] text-rs-light-gray">
              {groups.length === 0 ? "There are no groups yet. Add one below." : "No group matches the filter."}
            </p>
          )}
          <ul className="m-0 p-0 list-none flex flex-col gap-1.5">
            {shown.map((g) => (
              <li key={g.polymerGroupId}>
                <label className="flex items-center gap-2 text-[13px]">
                  <input
                    type="checkbox"
                    checked={selected.has(g.polymerGroupId)}
                    onChange={() => toggle(g.polymerGroupId)}
                  />
                  {g.name}
                </label>
              </li>
            ))}
          </ul>
        </div>

        {canAdd && (
          <div className="flex items-end gap-2 mb-4">
            <label className="flex flex-col gap-1.5 text-xs text-rs-light-gray flex-1">
              Not in the list? Add a group
              <Input
                value={newName}
                maxLength={120}
                placeholder="e.g. Ethylene Oxide (EO)"
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void add();
                  }
                }}
              />
            </label>
            <Btn variant="ghost" disabled={adding || !newName.trim() || !groups} onClick={() => void add()}>
              <span className="material-symbols-sharp">add</span>Add
            </Btn>
          </div>
        )}

        <p className="m-0 mb-3 text-[13px] text-rs-light-gray">
          {chosen.length === 0
            ? "No groups selected. The Groups column will be blank in the export."
            : `${chosen.length} selected`}
          {chosen.length > 0 && (
            <button
              className="bg-transparent border-none p-0 ml-3 text-rs-green"
              onClick={() => {
                setSelected(new Set());
                setNotice(null);
              }}
            >
              Clear all
            </button>
          )}
        </p>

        <div className="flex gap-2">
          <Btn
            disabled={!groups}
            onClick={() =>
              renamed ? onConfirm(chosen, { name: plantName.trim(), source: nameSource }) : onConfirm(chosen)
            }
          >
            <span className="material-symbols-sharp">{confirmIcon}</span>
            {confirmLabel}
          </Btn>
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
        </div>
      </Card>
    </div>
  );
}
